import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type {
  EnrichMasterFromLabelsResult,
  EnrichmentPreviewEntry,
} from "../../../api/client.ts";

interface EnrichFromLabelsDialogProps {
  open: boolean;
  onClose: () => void;
  blanks: EnrichmentPreviewEntry[];
  conflicts: EnrichmentPreviewEntry[];
  blocked: EnrichmentPreviewEntry[];
  selected: Record<string, boolean>;
  onToggle: (entryId: string, checked: boolean) => void;
  onSubmit: () => void;
  result: EnrichMasterFromLabelsResult | null;
  submitting: boolean;
  error: string | null;
}

function EnrichRow({
  entry,
  isConflict,
  checked,
  onToggle,
}: {
  entry: EnrichmentPreviewEntry;
  isConflict: boolean;
  /** Omitted for rows that cannot be applied (no checkbox). */
  checked?: boolean;
  onToggle?: (checked: boolean) => void;
}) {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, py: 0.5 }}>
      {onToggle ? (
        <Checkbox
          size="small"
          checked={!!checked}
          onChange={(e) => onToggle(e.target.checked)}
        />
      ) : (
        <Box sx={{ width: 38, flexShrink: 0 }} />
      )}
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {entry.company_name ?? entry.master_company_id}
        </Typography>
        {entry.fields.map((f) => (
          <Typography
            key={f.field}
            variant="caption"
            sx={{ color: "text.secondary", display: "block" }}
          >
            {isConflict
              ? `${f.field}: ${f.current ?? "—"} → ${f.proposed}`
              : `${f.field}: ${f.proposed}`}
          </Typography>
        ))}
        {entry.reason && (
          <Typography variant="caption" sx={{ color: "warning.main", display: "block" }}>
            {entry.reason}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default function EnrichFromLabelsDialog({
  open,
  onClose,
  blanks,
  conflicts,
  blocked,
  selected,
  onToggle,
  onSubmit,
  result,
  submitting,
  error,
}: EnrichFromLabelsDialogProps) {
  const companyName = (entryId: string) =>
    [...blanks, ...conflicts, ...blocked].find((e) => e.entry_id === entryId)
      ?.company_name ?? entryId;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Enrich master from labels</DialogTitle>
      <DialogContent>
        {result ? (
          <Stack spacing={1} sx={{ mt: 1 }}>
            <Alert
              severity={result.errors.length || result.skipped.length ? "warning" : "success"}
            >
              Updated {result.updated} master compan
              {result.updated === 1 ? "y" : "ies"}
              {result.noop > 0 && ` (${result.noop} unchanged)`}
              {result.skipped.length > 0 && ` — ${result.skipped.length} skipped`}
              {result.errors.length > 0 &&
                ` — ${result.errors.length} error${
                  result.errors.length === 1 ? "" : "s"
                }`}
              .
            </Alert>
            {result.skipped.map((e) => (
              <Typography key={e.entry_id} variant="caption" sx={{ color: "warning.main" }}>
                {companyName(e.entry_id)}: {e.message}
              </Typography>
            ))}
            {result.errors.map((e) => (
              <Typography key={e.entry_id} variant="caption" color="error">
                {companyName(e.entry_id)}: {e.message}
              </Typography>
            ))}
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Sync label values onto the linked master companies. Blanks are preselected;
              tick conflicts to overwrite the existing value.
            </Typography>
            {blanks.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Blanks ({blanks.length})
                </Typography>
                {blanks.map((e) => (
                  <EnrichRow
                    key={e.entry_id}
                    entry={e}
                    isConflict={false}
                    checked={!!selected[e.entry_id]}
                    onToggle={(c) => onToggle(e.entry_id, c)}
                  />
                ))}
              </Box>
            )}
            {conflicts.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Conflicts ({conflicts.length}) — current value differs from label
                </Typography>
                {conflicts.map((e) => (
                  <EnrichRow
                    key={e.entry_id}
                    entry={e}
                    isConflict={true}
                    checked={!!selected[e.entry_id]}
                    onToggle={(c) => onToggle(e.entry_id, c)}
                  />
                ))}
              </Box>
            )}
            {blocked.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Can't apply ({blocked.length})
                </Typography>
                {blocked.map((e) => (
                  <EnrichRow key={e.entry_id} entry={e} isConflict={false} />
                ))}
              </Box>
            )}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{result ? "Close" : "Cancel"}</Button>
        {!result && (
          <Button
            variant="contained"
            onClick={onSubmit}
            loading={submitting}
            disabled={Object.values(selected).every((v) => !v)}
          >
            Apply
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
